import React from "react";

import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimationOnScroll } from "react-animation-on-scroll";
import { Button, Card, Col, Container, Image, Row } from "react-bootstrap";
import { NavLink } from "react-router-dom";

import BannerComponent from "@/components/Banner.component";
import DocumentComponent from "@/components/Document.component";
import ScheduleComponent from "@/components/Schedule.component";
import SectionHeader from "@/components/SectionHeader.component";
import { getUrlLangPrefix } from "@/utils/Local";

import i18n from "../i18n";

interface IKitCard {
  title: string;
  description: string;
  img: string;
  link: string;
  cta: string;
}

interface IHighlight {
  title: string;
  description: string;
}

const MultiResidentialKits = (): React.JSX.Element => {
  const { t } = i18n;
  const langPrefix = getUrlLangPrefix();

  const kits: IKitCard[] = [
    {
      title: t("Triplex"),
      description: t(
        "Three units in a compact multi-storey format, suited to projects looking to add density with a repeatable building approach.",
      ),
      img: "/images/multi-residential-kits/Triplex.png",
      link: `${langPrefix}/${t("_route.contact-us")}`,
      cta: t("Request Triplex Details"),
    },
    {
      title: t("Sixplex"),
      description: t(
        "A six-unit building for developers planning more housing on a site or across a portfolio. Contact our team to discuss project requirements.",
      ),
      img: "/images/multi-residential-kits/Sixplex.png",
      link: `${langPrefix}/${t("_route.contact-us")}`,
      cta: t("Ask About the Sixplex"),
    },
  ];

  const highlights: IHighlight[] = [
    {
      title: t("Faster Assembly"),
      description: t(
        "Advanced composite panels shorten the on-site assembly of the building envelope.",
      ),
    },
    {
      title: t("Designed to Scale"),
      description: t(
        "Adapt a coordinated building approach across multiple lots or developments.",
      ),
    },
    {
      title: t("Energy Efficient"),
      description: t(
        "Continuous insulation and reduced thermal bridging support a high-performance envelope.",
      ),
    },
  ];

  const components: string[] = [
    t("PakVert™ wall and roof panels"),
    t("PakVille™ plumbing walls"),
    t("Floor systems"),
    t("Engineering coordination"),
    t("Connection components"),
    t("Installation support"),
  ];

  return (
    <React.Fragment>
      <DocumentComponent title={t("Multi-Residential Kits")} />
      <BannerComponent description="" title={t("Multi-Residential Kits")} />

      <Container>
        <Row>
          {kits.map((kit, index) => (
            <Col key={kit.title} className="mb-5 mb-lg-4" lg={6}>
              <Card className="p-0 h-100">
                <NavLink to={kit.link}>
                  <Card.Img
                    alt={kit.title}
                    className="modelCardImg"
                    loading={index === 0 ? "eager" : "lazy"}
                    src={kit.img}
                    variant="top"
                  />
                </NavLink>

                <Card.Body>
                  <Card.Title className="m-0 mb-2">
                    <h2 className="modelHeader text-start m-0">{kit.title}</h2>
                  </Card.Title>
                  <Card.Text>{kit.description}</Card.Text>
                </Card.Body>

                <Card.Footer
                  className="mb-2"
                  style={{ background: "unset", border: "unset" }}
                >
                  <NavLink to={kit.link}>
                    <Button className="btn-primary w-100">
                      {kit.cta}
                      <FontAwesomeIcon className="ms-2" icon={faChevronRight} />
                    </Button>
                  </NavLink>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      <Container className="mt-4 mb-5">
        <SectionHeader
          eyebrow={t("Built for Multi-Residential Development")}
          title={t("A Simpler Way to Deliver Multi-Unit Housing")}
          description={t(
            "PakVille™ combines coordinated building designs with advanced structural composite panel technology to simplify construction, accelerate on-site assembly and help developers scale from one building to an entire development.",
          )}
        />

        <Row className="featureHighlights mt-4">
          {highlights.map((item) => (
            <Col
              key={item.title}
              className="text-center mb-4 mb-md-0"
              md={4}
              xs={12}
            >
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </Col>
          ))}
        </Row>
      </Container>

      <Container className="flexContainer mt-4">
        <Row className="w-100 pt-2 pb-2 mt-2 mt-lg-0">
          <Col className="mt-4 mt-lg-0 order-2 order-lg-1" lg={6} xs={12}>
            <AnimationOnScroll animateOnce animateIn="animate__fadeInLeftBig">
              <div className="ratio ratio-16x9 text-center mt-4 mb-4 ">
                <iframe
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  className="embed-responsive-item w-100 rounded"
                  height="315"
                  src="https://www.youtube.com/embed/vG2Cv1pLTnE?si=Rey3is6a5AZaUlZc"
                  title={t("PakVille™ Home Kits")}
                  width="560"
                />
              </div>
            </AnimationOnScroll>
          </Col>

          <Col className="order-1 order-lg-2 ps-0 ps-lg-5" lg={6} xs={12}>
            <div
              className="flexContainer installationContent"
              style={{ gap: "8px" }}
            >
              <SectionHeader
                eyebrow={t("How It Comes Together")}
                title={t("From Foundation to Building Envelope")}
                description={t(
                  "See the sequence of a PakVille multi-residential build, from advanced composite panels and prefabricated components to on-site assembly.",
                )}
              />
            </div>
          </Col>
        </Row>
      </Container>

      <Container className="mt-4">
        <SectionHeader
          eyebrow={t("Project Scope")}
          title={t("A Coordinated Building-Kit Approach")}
          description={t(
            "PakVille can coordinate the core building-envelope components around your project. Confirm the exact supply and service scope in the project proposal.",
          )}
        />

        <Row className="kitComponents mt-4">
          {components.map((item) => (
            <Col key={item} className="text-center" md={4} xs={6}>
              <h3>{item}</h3>
            </Col>
          ))}
        </Row>
      </Container>

      <Container className="flexContainer mt-5 mb-5">
        <Row className="gx-5">
          <Col className="order-2 order-lg-1 mt-5 mt-lg-0" lg={5} xs={12}>
            <div className="flexContainer" style={{ flexDirection: "row" }}>
              <Row>
                <Col xs={6}>
                  <AnimationOnScroll
                    animateOnce
                    animateIn="animate__fadeInLeft"
                  >
                    <Image
                      rounded
                      alt={t("Traditional Wall Assembly (7 Components)")}
                      className="mw-100"
                      src="/images/technology/Traditional_Wall_Assembly.png"
                    />
                    <figcaption>
                      {t("Traditional Wall Assembly (7 Components)")}
                    </figcaption>
                  </AnimationOnScroll>
                </Col>

                <Col xs={6}>
                  <AnimationOnScroll
                    animateOnce
                    animateIn="animate__fadeInRight"
                  >
                    <Image
                      rounded
                      alt={t("PakVert™ Panel (1 panel replaces 7 components)")}
                      className="mw-100"
                      src="/images/technology/PakVert_all_in_one.png"
                    />
                    <figcaption>
                      {t("PakVert™ Panel (1 panel replaces 7 components)")}
                    </figcaption>
                  </AnimationOnScroll>
                </Col>
              </Row>
            </div>
          </Col>

          <Col className="order-1 order-lg-2" lg={7} xs={12}>
            <div
              className="flexContainer installationContent"
              style={{ gap: "8px" }}
            >
              <SectionHeader
                eyebrow={t("The Technology Behind The Kit")}
                title={t("Powered by PakVert™")}
                description={t(
                  "PakVert™ is an advanced structural composite panel technology that combines structure, insulation, vapor barrier and integrated finishes in one lightweight system. It helps simplify the building envelope while supporting energy-efficient construction.",
                )}
              />

              <NavLink to={`${langPrefix}/${t("_route.contact-us")}`}>
                <Button className="btn-primary">
                  {t("Ask About PakVert™")}
                </Button>
              </NavLink>
            </div>
          </Col>
        </Row>
      </Container>

      <ScheduleComponent />
    </React.Fragment>
  );
};

export default MultiResidentialKits;
